import moment from 'moment';
import get from 'lodash/get';
import { api } from '$app/api';
import { getAllGarages } from '$app/common/api';
import { attachRule, prioritizeRules } from '$app/shared/helpers/garageTimetable';
import { getTopLocalGarages } from './garageService';
import { getJobInviteCreatedBy, getJobInviteSentBy } from '../../../shared/getJobInviter';

async function searchOperationsByGarageIds(garageIds) {
  if (!garageIds.length) return {};

  const { data } = await api.post(`/garage-operations/garages/operations/search`, {
    garageIds,
  });

  return data;
}

async function getAcceptedInvitesForDay(garageIds, collectionDate) {
  if (!garageIds.length) return [];

  const start = moment(collectionDate).startOf('day').format('YYYY-MM-DDTHH:mm');
  const end = moment(collectionDate).endOf('day').format('YYYY-MM-DDTHH:mm');
  const { data } = await api.get(
    `/job-invitations/accepted?garageIds=${garageIds}&startDate=${start}&endDate=${end}`
  );

  return data;
}

async function getTimetableRules(timetableIds) {
  if (!timetableIds.length) return [];

  const { data } = await api.post(`/timetable/rules`, {
    eventType: 'BatchGetTimetableRulesRequest',
    body: { timetableIds },
  });

  return data;
}

export async function loadGaragesForBooking(booking) {
  const garageData = await getAllGarages(false, true);
  const collectionAddress = booking.collectionAddress || {};
  const deliveryAddress = booking.deliveryAddress || {};
  const collectionDate = booking.collectionDateTimeLocal;

  const coverageArea = await api.get(
    `/zones/areas/owners?type=garage&lat=${collectionAddress.latitude}&lng=${collectionAddress.longitude}`
  );

  const garages = getTopLocalGarages(garageData, coverageArea.data, collectionAddress, deliveryAddress);
  const garageIds = garages.map((garage) => garage.garageId);
  const timetableIds = garages.map((garage) => garage.timetableId);

  const [operationSearch, acceptedInvites, timetableRulesRaw] = await Promise.all([
    searchOperationsByGarageIds(garageIds),
    getAcceptedInvitesForDay(garageIds, collectionDate),
    getTimetableRules(timetableIds),
  ]);

  const timetableRules = Object.entries(timetableRulesRaw[0] || {});

  garages.forEach((garage) => {
    garage.operations = operationSearch[garage.garageId];

    const garageTimetableRules = timetableRules.find(([timetableId]) => timetableId === garage.timetableId);
    const [, rules = []] = garageTimetableRules || [];
    const dayTimetable = prioritizeRules(
      rules.map((rule) => attachRule(collectionDate, rule)).filter((rule) => rule)
    );

    const allocationData = acceptedInvites.find(({ garageId }) => garageId === garage.garageId);
    garage.currentRepairsAllocation = get(allocationData, 'acceptedRepairsBookings', 0);
    garage.currentMotAllocation = get(allocationData, 'acceptedMotBookings', 0);
    garage.currentCnd = get(allocationData, 'garageCollectionDeliveryCount', 0);
    garage.maxCnd = get(dayTimetable, 'maxCnd', 0);
    garage.maxMotCapacity = get(dayTimetable, 'maxMotCapacity', 0);
    garage.maxRepairsCapacity = get(dayTimetable, 'maxRepairsCapacity', 0);

    const iTimeFormat = 'HH:mm:ss';
    const oTimeFormat = 'h:mm a';
    garage.openingHour = dayTimetable ? moment(dayTimetable.startTime, iTimeFormat).format(oTimeFormat) : 'N/A';
    garage.closingHour = dayTimetable ? moment(dayTimetable.endTime, iTimeFormat).format(oTimeFormat) : 'N/A';
  });

  return { garages, garageOperations: operationSearch };
}

export async function loadGarageOperationsForInvites(booking) {
  const garageIds = (booking.garageInvites || [])
    .map((invite) => invite.garage?.garageId)
    .filter(Boolean);

  return searchOperationsByGarageIds(garageIds);
}

export async function createJobInvitation(bookingId, payload) {
  const dataToSend = {
    bookingId,
    ...payload,
  };

  const createdBy = getJobInviteCreatedBy(payload);
  const sentBy = getJobInviteSentBy(payload);
  if (sentBy !== undefined) dataToSend.sentBy = sentBy;
  if (createdBy !== undefined) dataToSend.createdBy = createdBy;

  return api.post(`/job-invitations`, dataToSend);
}

export async function patchJobInvitation(invitationId, payload) {
  const dataToSend = { ...payload };
  const sentBy = getJobInviteSentBy(payload);

  if (sentBy !== undefined) dataToSend.sentBy = sentBy;

  return api.patch(`/job-invitations/${invitationId}`, dataToSend);
}

export async function patchBooking(bookingId, payload) {
  return api.patch(`/bookings/${bookingId}`, payload);
}
